/**
 * Cristaux app bridge
 * ---------------------------------------------------------------------------
 * Restores CartItems.updateQuantityCallable(), a legacy cart method the Cristaux
 * app (shopify-app.cristaux.com/api/shopify/assets/scripts.js) calls for color
 * fill, multi-ship and cart reloads.
 *
 * Ported from old-theme-backup/assets/cart.js:273–347.
 * Extends Dawn's CartItems via prototype, so core cart.js stays untouched and
 * Dawn updates remain clean.
 *
 * Changes vs. original (defensive only, behaviour otherwise unchanged):
 *   - Null guards on quantityElement, section hosts and the cart errors element
 *   - `name &&` before the [name="..."] lookup (the app passes '')
 *   - Callback guarded with typeof check
 *   - Error logged in .catch
 *
 * Load order: must run AFTER Dawn's cart.js (defines CartItems) and BEFORE the
 * Cristaux app script. Both are loaded with defer from snippets/cristaux-app.liquid.
 *
 * Note: Dawn declares `class CartItems` at top level, which creates a global
 * binding but NOT a window property, so the check uses the bare name.
 */
/**
 * Guard 1: QuantityInput.validateQtyRules
 * The app injects <quantity-input> markup without Dawn 15.5's +/- buttons.
 * On insert, Dawn's connectedCallback → validateQtyRules() reads
 * `.quantity__button[name='minus'|'plus']` and throws on null, which aborts the
 * app's startup on /cart (cristauxCart never initialises → endless spinner).
 * Wrap it so a missing button is skipped instead of crashing.
 */
(() => {
  if (typeof QuantityInput === 'undefined') return;
  const proto = QuantityInput.prototype;
  if (!proto.validateQtyRules || proto.__cristauxGuarded) return;
  const original = proto.validateQtyRules;
  proto.validateQtyRules = function (...args) {
    try {
      return original.apply(this, args);
    } catch (error) {
      // App-injected quantity input without +/- buttons; nothing to toggle.
    }
  };
  proto.__cristauxGuarded = true;
})();

/**
 * Fix: Dawn cart quantity set to 0 (stock Dawn 15.x bug, not app-related)
 * Clicking − at quantity 1 (or typing 0) sends 0, which Dawn's validateQuantity
 * checks against the variant's minimum (1) and treats as an error instead of a
 * removal. Showing that error calls resetQuantityInput(), which only looks for
 * #Quantity-N (cart page) and crashes in the drawer (#Drawer-quantity-N), so the
 * item is never removed. Patched on the prototype so core cart.js stays stock.
 *   - 0 always means "remove this line" (any other value is validated as before)
 *   - resetQuantityInput finds the drawer input too
 */
(() => {
  if (typeof CartItems === 'undefined' || CartItems.prototype.__cristauxQtyZeroFix) return;
  const proto = CartItems.prototype;
  const originalValidate = proto.validateQuantity;
  if (typeof originalValidate !== 'function') return;

  proto.validateQuantity = function (event) {
    const input = event.target;
    if (parseInt(input.value) === 0) {
      input.setCustomValidity('');
      return this.updateQuantity(
        input.dataset.index,
        0,
        event,
        document.activeElement.getAttribute('name'),
        input.dataset.quantityVariantId
      );
    }
    return originalValidate.call(this, event);
  };

  proto.resetQuantityInput = function (id) {
    const input = this.querySelector(`#Quantity-${id}`) || this.querySelector(`#Drawer-quantity-${id}`);
    if (input) input.value = input.getAttribute('value');
    this.isEnterPressed = false;
  };

  proto.__cristauxQtyZeroFix = true;
})();

/**
 * Guard 2: CartItems.updateQuantityCallable (legacy method, see header above)
 */
(() => {
  if (typeof CartItems === 'undefined') {
    console.warn('[Cristaux bridge] CartItems not found; cart.js not loaded on this page.');
    return;
  }
  if (CartItems.prototype.updateQuantityCallable) return;

  CartItems.prototype.updateQuantityCallable = async function (line, quantity, name, variantId, callback) {
    this.enableLoading(line);

    const body = JSON.stringify({
      line,
      quantity,
      sections: this.getSectionsToRender().map((section) => section.section),
      sections_url: window.location.pathname,
    });

    return await fetch(`${routes.cart_change_url}`, { ...fetchConfig(), ...{ body } })
      .then((response) => response.text())
      .then((state) => {
        const parsedState = JSON.parse(state);
        const quantityElement =
          document.getElementById(`Quantity-${line}`) || document.getElementById(`Drawer-quantity-${line}`);
        const items = document.querySelectorAll('.cart-item');

        if (parsedState.errors) {
          if (quantityElement) quantityElement.value = quantityElement.getAttribute('value');
          this.updateLiveRegions(line, parsedState.errors);
          return;
        }

        this.classList.toggle('is-empty', parsedState.item_count === 0);
        const cartDrawerWrapper = document.querySelector('cart-drawer');
        const cartFooter = document.getElementById('main-cart-footer');
        if (cartFooter) cartFooter.classList.toggle('is-empty', parsedState.item_count === 0);
        if (cartDrawerWrapper) cartDrawerWrapper.classList.toggle('is-empty', parsedState.item_count === 0);

        this.getSectionsToRender().forEach((section) => {
          const host = document.getElementById(section.id);
          if (!host || !parsedState.sections?.[section.section]) return;
          const elementToReplace = host.querySelector(section.selector) || host;
          elementToReplace.innerHTML = this.getSectionInnerHTML(
            parsedState.sections[section.section],
            section.selector
          );
        });

        const updatedValue = parsedState.items[line - 1] ? parsedState.items[line - 1].quantity : undefined;
        let message = '';
        if (
          quantityElement &&
          items.length === parsedState.items.length &&
          updatedValue !== parseInt(quantityElement.value)
        ) {
          message =
            typeof updatedValue === 'undefined'
              ? window.cartStrings.error
              : window.cartStrings.quantityError.replace('[quantity]', updatedValue);
        }
        this.updateLiveRegions(line, message);

        const lineItem =
          document.getElementById(`CartItem-${line}`) || document.getElementById(`CartDrawer-Item-${line}`);
        if (lineItem && name && lineItem.querySelector(`[name="${name}"]`)) {
          cartDrawerWrapper
            ? trapFocus(cartDrawerWrapper, lineItem.querySelector(`[name="${name}"]`))
            : lineItem.querySelector(`[name="${name}"]`).focus();
        } else if (parsedState.item_count === 0 && cartDrawerWrapper) {
          trapFocus(cartDrawerWrapper.querySelector('.drawer__inner-empty'), cartDrawerWrapper.querySelector('a'));
        } else if (document.querySelector('.cart-item') && cartDrawerWrapper) {
          trapFocus(cartDrawerWrapper, document.querySelector('.cart-item__name'));
        }

        publish(PUB_SUB_EVENTS.cartUpdate, { source: 'cart-items', cartData: parsedState, variantId: variantId });
      })
      .catch((error) => {
        console.error('[Cristaux bridge] updateQuantityCallable failed:', error);
        this.querySelectorAll('.loading__spinner').forEach((overlay) => overlay.classList.add('hidden'));
        const errors = document.getElementById('cart-errors') || document.getElementById('CartDrawer-CartErrors');
        if (errors) errors.textContent = window.cartStrings.error;
      })
      .finally(() => {
        // Original order: callback first, then disableLoading.
        if (typeof callback === 'function') callback();
        this.disableLoading(line);
      });
  };
})();